import React from "react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { axiosInstance } from "@/pages/auth/auth.context";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BackLink } from "@/components/brand/BackLink";

const BonusBatchSchema = z.object({
  startingCheckNumber: z.number().min(1, "Must be at least 1"),
  notes: z.string().max(500, "Notes must be 500 characters or less"),
});

type BonusBatchFormValues = z.infer<typeof BonusBatchSchema>;

const CreateBonusBatchPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<BonusBatchFormValues>({
    resolver: zodResolver(BonusBatchSchema),
    defaultValues: {
      notes: "",
    },
  });

  const { mutate, isPending, error } = useMutation({
    mutationFn: async (values: BonusBatchFormValues) => {
      const { data } = await axiosInstance.post("/check-batches", {
        batchType: "BONUS",
        includeEmployees: true,
        includePartners: false,
        ...values,
      });
      return data;
    },
    onSuccess: async (batch) => {
      try {
        await axiosInstance.post(`/check-batches/${batch.id}/generate`);
      } catch (err) {
        let message = "There was an issue generating payments.";
        if (
          err &&
          typeof err === "object" &&
          "response" in err &&
          err.response?.data?.message
        ) {
          message = err.response.data.message;
        }
        toast({
          title: "Batch Generation Error",
          description: message,
          variant: "destructive",
        });
      }
      navigate(`/admin/check-batches/${batch.id}`);
    },
    onError: (err: any) => {
      toast({
        title: "Error",
        description: err?.response?.data?.message ?? "Failed to create batch.",
        variant: "destructive",
      });
    },
  });

  function onSubmit(values: BonusBatchFormValues) {
    mutate(values);
  }

  return (
    <div className="py-10 px-4">
      <div>
        <BackLink to="/admin/bonuses">Back to Bonuses</BackLink>
      </div>
      <div className="container max-w-md mx-auto">
        <h1 className="text-3xl font-bold text-foreground mb-2">
          Create Bonus Batch
        </h1>
        <p className="text-muted-foreground mb-8">
          Generates checks for all unpaid seasonal bonuses.
        </p>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="startingCheckNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Starting Check Number</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.valueAsNumber)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      className="textarea textarea-bordered w-full"
                      rows={3}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {error && (
              <div className="text-destructive">{(error as any).message}</div>
            )}
            <Button type="submit" className="w-full" disabled={isPending}>
              {isPending ? "Creating..." : "Create Bonus Batch"}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default CreateBonusBatchPage;
